import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../api";
import ChatAttachment from "./ChatAttachment";
import MessageReadStatus from "./MessageReadStatus";
import useChatMessages from "../hooks/useChatMessages";
import { attachmentError, chatError } from "../utils/chat";
import bgVideo from "../assets/bg.mp4";
function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
}
export default function TenantMessages() {
  const [thread, setThread] = useState(null),
    [draft, setDraft] = useState(''),
    [attachment, setAttachment] = useState(null),
    [opening, setOpening] = useState(true),
    [sending, setSending] = useState(false),
    [bootError, setBootError] = useState(''),
    [actionError, setActionError] = useState('');
  const alive = useRef(true),
    sendingRef = useRef(false),
    fileRef = useRef(null);
  const chat = useChatMessages(thread?.id);
  const {
    messages,
    scrollRef
  } = chat;
  const loading = opening || chat.loading,
    error = actionError || bootError || chat.error;
  const ensureThread = useCallback(async () => {
    setOpening(true);
    setBootError('');
    try {
      const res = await api.get('/chat/threads');
      let active = res.data?.items?.[0];
      if (!active) {
        const created = await api.post('/chat/threads', {});
        active = created.data?.thread;
      }
      if (!active?.id) throw new Error('No thread');
      if (alive.current) setThread(active);
    } catch {
      if (alive.current) setBootError('Unable to open your conversation. Please retry.');
    } finally {
      if (alive.current) setOpening(false);
    }
  }, []);
  useEffect(() => {
    alive.current = true;
    ensureThread();
    return () => {
      alive.current = false;
    };
  }, [ensureThread]);
  const handleSend = async event => {
    event.preventDefault();
    if (!thread?.id || sendingRef.current || (!draft.trim() && !attachment)) return;
    const invalid = attachmentError(attachment);
    if (invalid) {
      setActionError(invalid);
      return;
    }
    sendingRef.current = true;
    setSending(true);
    setActionError('');
    try {
      const data = new FormData();
      if (draft.trim()) data.append('body', draft.trim());
      if (attachment) data.append('attachment', attachment);
      await api.post('/chat/threads/' + thread.id + '/messages', data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (!alive.current) return;
      setDraft('');
      setAttachment(null);
      if (fileRef.current) fileRef.current.value = '';
      await chat.refresh();
    } catch (error) {
      if (alive.current) setActionError(chatError(error, 'Failed to send message. Your draft is saved; please retry.'));
    } finally {
      sendingRef.current = false;
      if (alive.current) setSending(false);
    }
  };
  const chooseAttachment = event => {
    const file = event.target.files?.[0] || null;
    const invalid = attachmentError(file);
    if (invalid) {
      setActionError(invalid);
      event.target.value = '';
      return;
    }
    setAttachment(file);
    setActionError('');
  };
  return <div className="relative min-h-screen overflow-hidden bg-slate-950 font-sans text-white">
      <video data-workspace-background="true" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" src={bgVideo} autoPlay muted loop playsInline />
      <div className="absolute inset-0 bg-slate-950/86" />

      <div className="workspace-page-body relative z-10 min-h-screen px-4 py-16 sm:px-6 md:py-10 lg:px-8">
        <div className="mb-5 border-b border-white/10 pb-5">
          <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">
            Support Chat
          </p>
          <h1 className="mt-2 text-3xl font-bold tracking-wide md:text-4xl">
            Messages
          </h1>
          <p className="mt-2 text-sm text-slate-300 md:text-base">
            Send questions and updates directly to property management.
          </p>
        </div>

        <section className="flex h-[70vh] min-h-[420px] flex-col rounded-lg border border-white/10 bg-slate-950/90 shadow-xl">
          <div className="border-b border-white/10 px-4 py-3">
            <p className="text-lg font-semibold text-white">Management</p>
            <p className="text-xs text-slate-400">Replies refresh automatically while this page is open.</p>
          </div>

          <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {chat.hasOlder && <button type="button" className="ws-button ws-button-secondary" disabled={chat.loadingOlder} onClick={chat.loadOlder}>{chat.loadingOlder ? "Loading…" : "Load older messages"}</button>}
            {loading ? <p className="text-sm text-slate-400">Loading conversation...</p> : messages.length === 0 ? <p className="text-sm text-slate-400">No messages yet. Start by sending one.</p> : messages.map(message => {
            const mine = message.sender_role === "tenant";
            return <div key={message.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-lg border px-3 py-2 text-sm ${mine ? "border-teal-300/40 bg-teal-500/15 text-teal-100" : "border-white/10 bg-white/10 text-slate-100"}`}>
                      {message.body && <p className="whitespace-pre-wrap break-words">{message.body}</p>}
                      {message.attachment_url && <ChatAttachment message={message} />}
                      <p className="mt-1 text-[11px] text-slate-300/80">{formatDateTime(message.created_at)}{mine && <MessageReadStatus message={message} />}</p>
                    </div>
                  </div>;
          })}
          </div>

          <form onSubmit={handleSend} className="border-t border-white/10 px-4 py-3">
            {error && <div role="alert" className="mb-2 rounded-lg border border-red-300/30 bg-red-500/15 px-3 py-2 text-sm text-red-100">
                {error} <button type="button" className="underline" onClick={() => {
              thread?.id ? chat.refresh() : ensureThread();
            }}>Reload</button>
              </div>}

            <div className="mb-2 grid gap-2 md:grid-cols-[minmax(0,1fr)_auto]">
              <textarea aria-label="Message" maxLength={4000} disabled={sending} value={draft} onChange={e => setDraft(e.target.value)} rows={2} placeholder="Type your message..." className="w-full resize-none rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30" />
              <label className="cursor-pointer rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold text-slate-200 transition hover:bg-white/10">
                Attach
                <input ref={fileRef} disabled={sending} type="file" accept=".jpg,.jpeg,.png,.pdf" className="sr-only" onChange={chooseAttachment} />
              </label>
            </div>
            {attachment && <p className="mb-2 text-xs text-slate-300">Attachment: {attachment.name} <button type="button" disabled={sending} className="underline" onClick={() => {
              setAttachment(null);
              if (fileRef.current) fileRef.current.value = "";
            }}>Remove attachment</button></p>}
            <button type="submit" disabled={loading || sending || !thread?.id || (!draft.trim() && !attachment)} className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-500 disabled:opacity-60">
              {sending ? "Sending..." : "Send"}
            </button>
          </form>
        </section>
      </div>
    </div>;
}
